import type { RuntimeConfig } from '@/shared/types/runtimeConfig.types';

// ---------------------------------------------------------------------------
// Runtime config keys that provide external URLs.
// Keep in sync with CONFIG_KEYS in runtimeConfigService.ts.
// ---------------------------------------------------------------------------
export const RUNTIME_URL_KEYS = {

  PRIMARY_HOST: 'supervisor.frontend.primaryhost',
  /** Full URL for the EcoVision / Vestige external application */
  ECOVISION_URL: 'component.vestige.url',
} as const;

// ---------------------------------------------------------------------------
// Static SPA routes — only relative paths, no external hostnames.
// ---------------------------------------------------------------------------
export const AppRoutes = {
  noAccess: '/no-access',
  login: '/login',
  forgotPassword: '/forgot-password',
  lostPassword: (token: string) => `/lost_password/${token}`,
  dashboard: '/dashboard',
  /** Bookmark alias; redirects to the Fleet dashboard (dashboardMfe). */
  home: '/home',
  newTrip: (clientId: string | number) => `/new-trip/${clientId}`,
  // newTripConfirmation removed — /new-trip/:clientId/confirmation has no registered route handler.
  // Use newTripV2Confirmation for the new round-trip flow.
  newTripV2: (clientId: string | number) => `/new-trip-v2/${clientId}`,
  newTripV2Confirmation: (clientId: string | number) => `/new-trip-v2/${clientId}/confirmation`,
  // Subscription entry point for New Trip v2. A distinct path (rather than router
  // state) so the mode survives a refresh or a shared link, and so the route can
  // carry its own permission guard — see newTripV2Subscription in operationsRoutes.
  newTripV2Subscription: (clientId: string | number) => `/new-trip-v2/${clientId}/subscription`,
  schedules: '/operations/schedules',
  scheduleDetails: (id: string | number) => `/operations/schedules/${id}`,
  scheduleVersions: (id: string | number) => `/operations/schedules/${id}/versions`,
  schedulePrintManifest: (runId: string | number, scheduleId: string | number, printDate: string) =>
    `/operations/schedules/${scheduleId}/print-manifest?print_date=${printDate}&run_id=${runId}`,
  schedulesMarkComplete: '/operations/schedules/mark-complete',
  trips: '/operations/trips',
  tripDetails: (tripId: string | number) => `/operations/trips/${tripId}`,
  tripSchedulingLog: (schedulingExplanationId: string | number) => `/operations/trip-scheduling-log/${schedulingExplanationId}`,
  subscriptions: '/operations/subscriptions',
  subscriptionsDetails: (id: string | number) => `/operations/subscriptions/${id}`,
  subscriptionConfirmation: '/operations/subscriptions/confirmation',
  reimbursements: '/operations/reimbursements',
  reimbursementDetails: (id: string | number) => `/operations/reimbursements/${id}`,
  expenses: '/operations/expenses',
  expenseDetails: (id: string | number) => `/operations/expenses/${id}`,
  archiveSchedules: '/operations/archive-schedules',
  archiveSchedulesDetails: (id: string | number) => `/operations/archive-schedules/${id}`,
  archiveSchedulesMarkComplete: '/operations/archive-schedules/mark-complete',
  archiveTrips: '/operations/archive-trips',
  archiveTripsDetails: (id: string | number) => `/operations/archive-trips/${id}`,
  /** Deep-link to archive trips filtered by a specific rider policy violation ID. */
  archiveTripsForViolation: (violationId: string | number) =>
    `/operations/archive-trips?q.rider_policy_violation=${violationId}`,
  copyArchiveTrip: (tripId: string | number) => `/admin/trips/${tripId}/archive-copy`,
  returnArchiveTrip: (tripId: string | number) => `/operations/archive-trips/${tripId}/return`,
  archiveTripsReconcile: '/operations/archive-trips/reconcile',
  archiveTripsReview: '/operations/archive-trips/review',
  archiveTripsVerify: '/operations/archive-trips/verify',
  archiveTripsAdvanced: '/operations/archive-trips/advanced',
  fleet: '/fleet',
  // Federated Dispatcher MFE (extracted 6.2 Fleet Dashboard). Splat-mounted:
  // the remote owns everything below this prefix. The bare '/dashboard' leaf
  // route redirects here (ECOPD-18453).
  dashboardMfe: '/dashboard/fleet',
  /** Pre-2026-09-23 mount path; redirected to dashboardMfe for old bookmarks. */
  dashboardMfeLegacy: '/dashboard-mfe',
  riders: '/riders',
  scheduling: '/scheduling',

  // Operations
  operations: {
    dashboard: '/operations/dashboard',
    liveMap: '/operations/live-map',
    runStatus: '/operations/runStatuses',
    onTimePerformance: '/operations/onTimePerformances',
    lateTrips: '/operations/lateTrips',
    alerts: '/operations/alerts',
    messages: '/operations/messages',
    inspectionLists: '/operations/archiveInspectionChecklists',
    inspectionListDetails: (id: string | number) => `/operations/archiveInspectionChecklists/${id}`,
    archiveInspectionItems: '/operations/archiveInspectionItems',
  },

  // Administration
  admin: {
    providers: '/admin/providers',
    trips: '/admin/trips',
    providerDetails: (providerId: string | number) => `/admin/provider/${providerId}`,
    clients: '/admin/clients',
    clientDetails: (clientId: string | number) => `/admin/clients/${clientId}`,
    tripDetails: (tripId: string | number) => `/admin/trips/${tripId}`,
    rebookTrip: (tripId: string | number) => `/admin/trips/${tripId}/rebook`,
    copyTrip: (tripId: string | number) => `/admin/trips/${tripId}/copy`,
    returnTrip: (tripId: string | number) => `/admin/trips/${tripId}/return`,
    returnSubscription: (subscriptionId: string | number) => `/operations/subscriptions/${subscriptionId}/return`,
    newReimbursement: (clientId: string | number) => `/admin/clients/${clientId}/reimbursements/new`,
    reimbursementDetails: (clientId: string | number, reimbursementId: string | number) => `/admin/clients/${clientId}/reimbursements/${reimbursementId}`,
    newSubscription: (clientId: string | number) => `/admin/clients/${clientId}/subscriptions/new`,
    subscriptionConfirmation: (clientId: string | number) => `/admin/clients/${clientId}/subscriptions/confirmation`,
    selfServiceAccounts: '/admin/self-service-accounts',
    selfServiceAccountDetails: (id: string | number) => `/admin/self-service-accounts/${id}`,
    selfServiceAccountHistory: (id: string | number) => `/admin/self-service-accounts/${id}/history`,
    runs: '/admin/runs',
    runDetails: (id: string | number) => `/admin/runs/${id}`,
    /**
     * Deep-link to the Schedule tab.
     *
     * Uses the legacy q.range_default_enum / q.range_default_date param names
     * that RunDetails#readScheduleFiltersFromUrl reads to initialise the
     * schedule filter on page load.
     *
     * `timePeriod` accepts: today | yesterday | tomorrow | time
     * When `timePeriod` is "time", `date` (YYYY-MM-DD) must also be provided.
     *
     * `stopRangeEnum` accepts: recent | after | range (optional).
     * Maps to q.stop_range_enum — controls the stop-time filter on the schedule tab.
     */
    runScheduleTab: (id: string | number, timePeriod?: string, date?: string, stopRangeEnum?: string) => {
      const params = new URLSearchParams();
      params.set('tab', 'schedule');
      if (timePeriod) {
        params.set('q.range_default_enum', timePeriod);
        if (timePeriod === 'time' && date) {
          params.set('q.range_default_date', date);
        }
      }
      if (stopRangeEnum) {
        params.set('q.stop_range_enum', stopRangeEnum);
      }
      return `/admin/runs/${id}?${params.toString()}`;
    },
    runPrintManifest: (id: string | number, printDate: string) =>
      `/admin/runs/${id}/print-manifest?print_date=${printDate}`,
    vehicles: '/admin/vehicles',
    vehicleDetails: (id: string | number) => `/admin/vehicles/${id}`,
    /**
     * Vehicle details by PUBLIC id. The details page resolves the public id to
     * the internal id and canonicalizes the URL (mirrors drt_ui's
     * `EcoId.id_attr(:public_id, …)` redirect + RunDetails). Use this when only
     * the vehicle public id is known (e.g. archive-sourced dashboard widgets)
     * — vehicle public ids can be numeric, so `?idType=public_id` disambiguates
     * them from an internal id rather than guessing.
     */
    vehicleDetailsByPublicId: (publicId: string | number) =>
      `/admin/vehicles/${publicId}?idType=public_id`,
    vehicleHistory: (id: string | number) => `/admin/vehicles/${id}/history`,
    vehicleTypes: '/admin/vehicle-types',
    drivers: '/admin/drivers',
    driverDetails: (driverId: string | number) => `/admin/drivers/${driverId}`,
    driverHistory: (driverId: string | number) => `/admin/drivers/${driverId}/history`,
    templateRoutes: '/admin/template-routes',
    inspections: '/admin/inspections',
    inspectionDetails: (id: string | number) => `/admin/inspections/${id}`,
    providerInspectionForms: '/admin/inspections/provider-forms',
    vehicleInspectionForms: '/admin/inspections/vehicle-forms',
    maintenanceRecords: '/admin/maintenance-records',
    permissions: '/admin/permissions',
    serviceRecords: '/admin/service-records',
    userAccounts: '/admin/user-accounts',
    userGroups: '/admin/user-groups',
    userRoles: '/admin/user-roles',
    billing: {
      base: '/admin/fundingAndBilling',
      billingCodes: '/admin/billing-codes',
      billingCodeDetails: (id: string | number) => `/admin/billing-codes/${id}`,
      billingCodeHistory: (id: string | number) => `/admin/billing-codes/${id}/history`,
      billingCodeSets: '/admin/billing-code-sets',
      billingCodeSetDetails: (id: string | number) => `/admin/billing-code-sets/${id}`,
      billingCodeSetHistory: (id: string | number) => `/admin/billing-code-sets/${id}/history`,
      fundingSources: '/admin/funding-sources',
      fundingSourceDetails: (fundingSourceId: string | number) => `/admin/funding-sources/${fundingSourceId}`,
      fundingSourceEligibilityDetails: (clientId: string | number, customerDecisionId: string | number) => `/admin/clients/${clientId}/funding-eligibility/${customerDecisionId}`,
      fundingSourceServiceRules: (fundingSourceId: string | number) => `/admin/funding-sources/${fundingSourceId}/service-rules`,
      fundingSourceHistory: (fundingSourceId: string | number) => `/admin/funding-sources/${fundingSourceId}/history`,
      tripPurpose: '/admin/trip-purpose',
      providerRates: '/admin/provider-rates',
      providerRateDetails: (id: string | number) => `/admin/provider-rates/${id}`,
      providerRateCopy: (id: string | number) => `/admin/provider-rates/${id}/copy`,
      providerRateHistory: (id: string | number) => `/admin/provider-rates/${id}/history`,
      surcharges: '/admin/surcharges',
      surchargeDetails: (id: string | number) => `/admin/surcharges/${id}`,
    },
    tripGroups: '/admin/trip-groups',
    tripGroupDetails: (id: string | number) => `/admin/trip-groups/${id}`,
    fixedRoutes: '/admin/fixed-routes',
    fixedRouteDetails: (id: string | number) => `/admin/fixed-routes/${id}`,
    runPools: '/admin/run-pools',
    calendars: '/admin/calendars',
    calendersdetails: (calendarId: string | number) => `/admin/calendars/${calendarId}`,
    // Centers live under /system/centers (see system.centers below). These
    // aliases are kept because multiple client-detail references still read
    // `AppRoutes.admin.centers` — pointing them at the real route avoids a
    // wide-spread rename and closes the 404 regression (#105, 2026-04-17).
    centers: '/system/centers',
    centersdetails: (centerId: string | number) => `/system/centers/${centerId}`,
    providerDenyList: '/admin/provider-deny-list',
  },

  // Tools — static SPA routes only; external tool URLs live in getDynamicAppRoutes()
  tools: {
    exportFiles: '/tools/export-files',
    tripExport: '/tools/trip-export',
    archiveTripExport: '/tools/archive-trip-export',
    tripExportProfiles: '/tools/trip-export-profiles',
    feedback: '/tools/feedback',
    roadcalls: '/tools/roadcalls',
    driverIncidents: '/tools/driver-incidents',
    driverIncidentDetails: (id: string | number) => `/tools/driver-incidents/${id}`,
    vehicleMaintenance: '/tools/vehicle-maintenance',
    driverAdditionalInfo: '/tools/driver-additional-info',
    records: '/tools/records',
    riderPolicyViolations: '/tools/rider-policy-violations',
    riderPolicyViolationDetails: (id: string | number) => `/tools/rider-policy-violations/${id}`,
    riderPolicyPotentialViolations: '/tools/rider-policy-potential-violations',
    exportFieldRules: '#',
  },

  // System
  system: {
    locations: '/system/locations',
    locationDetails: (locationId: string | number) => `/system/locations/${locationId}`,
    streetSegments: '/system/street-segments',
    streetSegmentDetails: (segmentId: string | number) => `/system/street-segments/${segmentId}`,
    devices: '/system/devices',
    deviceDetails: (id: string | number) => `/system/devices/${id}`,
    mdtDevices: '/system/devices/mdt-devices',
    messageTemplates: '/system/message-templates',
    serviceAreas: '/system/service-areas',
    serviceAreaDetails: (id: string | number) => `/system/service-areas/${id}`,
    polygons: '/system/polygons',
    polygonDetails: (polygonId: string | number) => `/system/polygons/${polygonId}`,
    polygonHistory: (polygonId: string | number) => `/system/polygons/${polygonId}/history`,
    users: '/system/users',
    userDetails: (userId: string | number) => `/system/users/${userId}`,
    userDetailsTab: (userId: string | number) => `/system/users/${userId}/details`,
    userHistoryTab: (userId: string | number) => `/system/users/${userId}/history`,
    events: {
      base: '#',
      userEvents: '/system/user-events',
      scheduleEvents: '/system/schedule-events',
      tripEvents: '/system/trip-events',
      subscriptionEvents: '/system/subscription-events',
      vehicleEvents: '/system/vehicle-events',
      reimbursementEvents: '/system/reimbursement-events',
      batchJobEvents: '/system/batch-job-events',
    },
    configuration: {
      base: '#',
      parameters: '/system/configuration',
      requiredFields: '/system/required-fields',
      customValues: '/system/custom-values',
      scheduleCustomFields: '/system/schedule-custom-fields',
      reportTemplates: '/system/report-templates',
      batchJobs: '/system/batch-jobs',
      batchJobDetails: (id: string | number) => `/system/batch-jobs/${id}`,
      batchJobDetailsTab: (id: string | number) => `/system/batch-jobs/${id}/details`,
      batchJobEventsTab: (id: string | number) => `/system/batch-jobs/${id}/events`,
      batchJobHistoryTab: (id: string | number) => `/system/batch-jobs/${id}/history`,
    },
    filterSet: '/system/filterset',
    exportFiles: '/system/export-files',
    userRoles: '/system/user-roles',
    userRoleDetails: (roleId: string | number) => `/system/user-roles/${roleId}`,
    userRoleEdit: (roleId: string | number) => `/system/user-roles/${roleId}/edit`,
    newUserRole: '/system/user-roles/new',
    centers: '/system/centers',
    centerDetails: (centerId: string | number) => `/system/centers/${centerId}`,
    userPreferences: '/system/user-preferences',
  },

  // User — static help URLs kept here as public, tenant-independent constants.
  // If these ever become tenant-specific, move them to getDynamicAppRoutes().
  user: {
    profile: '#',
    preferences: '#',
    help: '#',
    signOut: '/login',
    viewDocumentations: 'https://helpdesk.ecolane.com/hc/en-us',
    report: 'https://ecolane-report-help-pages.s3.amazonaws.com/Ecolane_Reporting+System_Online+Help/index.htm',
    getSupport: 'https://helpdesk.ecolane.com/hc/en-us/requests/new',
    releaseNotes: 'https://helpdesk.ecolane.com/hc/en-us/articles/13519246779283-Release-Notes',
  },
};

// ---------------------------------------------------------------------------
// Dynamic external URLs — constructed at runtime from supervisor.frontend.primaryhost
// and other runtime config values.
// All URLs in this object MUST be opened with target="_blank" rel="noopener noreferrer".
// ---------------------------------------------------------------------------

export interface DynamicAppRoutes {
  /** EcoVision / Vestige application — full URL from component.vestige.url */
  ecovision: string;
  /** Eligibility management (legacy frontend) */
  eligibility: string;
  /** Bulk / order import */
  bulkImport: string;
  /** Order import (canonical alias for bulkImport) */
  orderImport: string;
  /** Trip completion report upload */
  tripCompletionReport: string;
  /** Archive trip import */
  archiveTripImport: string;
  /** Reporting system */
  reporting: string;
  /** EDI service */
  edi: string;
  /** Insights dashboard */
  insights: string;
}

/**
 * Builds dynamic external URLs from runtime configuration.
 *
 * Call this wherever you need an external URL that depends on the tenant's
 * primary host or other runtime settings.  The result changes per tenant.
 *
 * @param runtimeConfig - Value from useRuntimeConfig().config
 * @returns DynamicAppRoutes with fully-qualified external URLs
 *
 * @example
 * const { config } = useRuntimeConfig();
 * const dynamic = getDynamicAppRoutes(config);
 * window.open(dynamic.reporting, '_blank', 'noopener,noreferrer');
 */
export const getDynamicAppRoutes = (
  runtimeConfig: RuntimeConfig | null | undefined,
): DynamicAppRoutes => {
  const getString = (key: string, fallback = ''): string => {
    if (!runtimeConfig) return fallback;
    const val = runtimeConfig[key];
    return val !== undefined && val !== null ? String(val) : fallback;
  };

  // Normalise: strip trailing slash so we can append paths uniformly.
  const rawHost = getString(RUNTIME_URL_KEYS.PRIMARY_HOST, '');
  const primaryHost = rawHost.endsWith('/') ? rawHost.slice(0, -1) : rawHost;

  return {
    ecovision: getString(RUNTIME_URL_KEYS.ECOVISION_URL, ''),
    eligibility: `${primaryHost}/eligibility`,
    bulkImport: `${primaryHost}/orderimport/`,
    orderImport: `${primaryHost}/orderimport/`,
    tripCompletionReport: `${primaryHost}/orderimport/uploaded_trip_completion_files`,
    archiveTripImport: `${primaryHost}/orderimport/uploaded_trip_completion_files`,
    reporting: `${primaryHost}/reporting/reports`,
    edi: `${primaryHost}/ediservice`,
    insights: `${primaryHost}/drt/pages/insights`,
  };
};